//import { data } from "react-router-dom";
import api from './axiosInstance'

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authApi = {
  login: data => api.post('/auth/login', data),
  register: data => api.post('/auth/register', data),
  refresh: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
  sendOtp: email => api.post('/auth/send-otp', { email }),
  verifyOtp: (email, otp) => api.post('/auth/verify-otp', { email, otp }),
  forgotPassword: data => api.post('/auth/forgot-password', data),
  verifyResetToken: token =>
    api.get('/auth/verify-reset-token', { params: { token } }),
  resetPassword: data => api.post('/auth/reset-password', data)
}

// ── Password Setup (no auth required — token in body/query) ──────────────────
export const passwordApi = {
  /**
   * Verify that a setup token is still valid.
   * GET /api/auth/verify-setup-token?token=<token>
   */
  verifySetupToken: token =>
    api.get('/auth/verify-setup-token', { params: { token } }),

  /**
   * Submit the new password with the single-use token.
   * POST /api/auth/setup-password  { token, password }
   */
  setupPassword: (token, password) =>
    api.post('/auth/setup-password', { token, password })
}

// ── Tenants ───────────────────────────────────────────────────────────────────
export const tenantApi = {
  getAll: (page = 1) => api.get('/tenants', { params: { page } }),
  create: data => api.post('/tenants', data),
  delete: id => api.delete(`/tenants/${id}`)
}

// ── Users ─────────────────────────────────────────────────────────────────────
export const userApi = {
  getAll: ({ tenantId, orgId, page = 1 } = {}) =>
    api.get('/users', { params: { tenant_id: tenantId, org_id: orgId, page } }),

  create: data => api.post('/users', data),

  /** Super-admin creates a user for a specific org/tenant. */
  createForOrg: data =>
    api.post('/users', data, { params: { tenant_id: data.tenant_id } }),

  update: (id, data) => api.put(`/users/${id}`, data),
  delete: id => api.delete(`/users/${id}`),

  /** Re-send the password setup invitation email. */
  resendInvitation: id => api.post(`/users/${id}/resend-invitation`)
}

// ── Locations ─────────────────────────────────────────────────────────────────
export const locationApi = {
  getAll: ({ tenantId } = {}) =>
    api.get('/locations', { params: { tenant_id: tenantId } }),
  create: (data, tenantId) =>
    api.post('/locations', data, { params: { tenant_id: tenantId } }),
  update: (id, data, tenantId) =>
    api.put(`/locations/${id}`, data, { params: { tenant_id: tenantId } }),
  delete: (id, tenantId) =>
    api.delete(`/locations/${id}`, { params: { tenant_id: tenantId } })
}

// ── Assets ────────────────────────────────────────────────────────────────────
export const assetApi = {
  getAll: ({ tenantId, page = 1 } = {}) =>
    api.get('/assets', { params: { tenant_id: tenantId, page } }),
  // FIX: accept tenantId so super_admin requests include the tenant_id query param
  create: (data, tenantId) =>
    api.post('/assets', data, { params: tenantId ? { tenant_id: tenantId } : undefined }),
  update: (id, data, tenantId) =>
    api.put(`/assets/${id}`, data, { params: tenantId ? { tenant_id: tenantId } : undefined }),
  setActive: (id, isActive, tenantId) =>
    api.patch(`/assets/${id}/status`, { is_active: isActive },
      { params: tenantId ? { tenant_id: tenantId } : undefined }),
  delete: (id, tenantId) =>
    api.delete(`/assets/${id}`, { params: tenantId ? { tenant_id: tenantId } : undefined })
}

// ── Organizations ─────────────────────────────────────────────────────────────
export const organizationApi = {
  // ── Tenant-scoped sub-org management (admin within a tenant) ──────────────
  getAll: ({ tenantId, parentId } = {}) =>
    api.get('/organizations', {
      params: {
        tenant_id: tenantId,
        ...(parentId != null ? { parent_id: parentId } : {})
      }
    }),
  create: (data, tenantId) =>
    api.post('/organizations', data, { params: { tenant_id: tenantId } }),
  update: (id, data, tenantId) =>
    api.put(`/organizations/${id}`, data, { params: { tenant_id: tenantId } }),
  delete: (id, tenantId) =>
    api.delete(`/organizations/${id}`, { params: { tenant_id: tenantId } }),

  // ── Super-admin client-org management ─────────────────────────────────────
  /** Fetch all root client orgs created by the logged-in super_admin. */
  getClients: () =>
    api.get('/organizations/clients'),

  /** Create a new client org (+ tenant) — body: { company_name, phone_number, address, shift_start_time, shift_end_time } */
  createClient: data =>
    api.post('/organizations/client', data),

  /** Update profile details of a root client org. */
  updateClient: (id, data) =>
    api.put(`/organizations/${id}/client`, data),

  /** Activate / deactivate a client org (super_admin only). */
  setActive: (id, isActive) =>
    api.patch(`/organizations/${id}/status`, { is_active: isActive }),

  /** Delete a root client org and its tenant. */
  deleteClient: id =>
    api.delete(`/organizations/${id}/client`),

  /** Upload a logo image for a client org (FormData with field 'logo'). */
  uploadLogo: (id, formData) =>
    api.post(`/organizations/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

// ── IoT Controllers ───────────────────────────────────────────────────────────
export const iotControllerApi = {
  getAll: tenantId =>
    api.get('/iot-controllers', { headers: { 'X-Tenant-Id': tenantId } }),
  getOne: (id, tenantId) =>
    api.get(`/iot-controllers/${id}`, { headers: { 'X-Tenant-Id': tenantId } }),
  create: (data, tenantId) =>
    api.post('/iot-controllers', data, {
      headers: { 'X-Tenant-Id': tenantId }
    }),
  update: (id, data, tenantId) =>
    api.put(`/iot-controllers/${id}`, data, {
      headers: { 'X-Tenant-Id': tenantId }
    }),
  delete: (id, tenantId) =>
    api.delete(`/iot-controllers/${id}`, {
      headers: { 'X-Tenant-Id': tenantId }
    }),
  setActive: (id, isActive, tenantId) =>
    api.patch(
      `/iot-controllers/${id}/status`,
      { is_active: isActive },
      { headers: { 'X-Tenant-Id': tenantId } }
    ),
  assignLocation: (id, locationId, tenantId) =>
    api.patch(
      `/iot-controllers/${id}/location`,
      { location_id: locationId },
      { headers: { 'X-Tenant-Id': tenantId } }
    ),
  // FIX: asset assignment uses same header pattern as all other controller endpoints
  assignAsset: (id, assetIds, tenantId) =>
    api.patch(
      `/iot-controllers/${id}/asset`,
      { asset_ids: assetIds },
      { headers: { 'X-Tenant-Id': tenantId } }
    ),
  getLivePayload: (id, tenantId) =>
    api.get(`/iot-controllers/${id}/live-payload`, {
      headers: { 'X-Tenant-Id': tenantId }
    }),
  getParams: (id, tenantId) =>
    api.get(`/iot-controllers/${id}/params`, {
      headers: { 'X-Tenant-Id': tenantId }
    }),
  saveParams: (id, mappings, tenantId) =>
    api.put(`/iot-controllers/${id}/params`, mappings, {
      headers: { 'X-Tenant-Id': tenantId }
    })
}

// ── Departments ───────────────────────────────────────────────────────────────
export const departmentApi = {
  getAll: ({ tenantId, locationId, orgId } = {}) =>
    api.get('/departments', {
      params: {
        tenant_id: tenantId,
        ...(locationId ? { location_id: locationId } : {}),
        ...(orgId ? { org_id: orgId } : {}),
      },
    }),
  create: (data, tenantId) =>
    api.post('/departments', data, { params: { tenant_id: tenantId } }),
  update: (id, data, tenantId) =>
    api.put(`/departments/${id}`, data, { params: { tenant_id: tenantId } }),
  delete: (id, tenantId) =>
    api.delete(`/departments/${id}`, { params: { tenant_id: tenantId } }),
}

// ── Parameter Library ─────────────────────────────────────────────────────────
export const parameterLibraryApi = {
  getAll: () => api.get('/parameter-library')
}

// ── Sensor Data ───────────────────────────────────────────────────────────────
export const sensorDataApi = {
  getLatest: tenantId =>
    api.get('/sensor-data/latest', { headers: { 'X-Tenant-Id': tenantId } }),
  getRecent: (controllerId, tenantId, limit = 60) =>
    api.get(`/sensor-data/${controllerId}/recent`, {
      params: { limit },
      headers: { 'X-Tenant-Id': tenantId }
    }),
  getHistory: (controllerId, tenantId, hours = 1) =>
    api.get(`/sensor-data/${controllerId}/history`, {
      params: { hours },
      headers: { 'X-Tenant-Id': tenantId }
    }),
  getRange: (controllerId, tenantId, from, to, limit = 1000) =>
    api.get(`/sensor-data/${controllerId}/range`, {
      params: { from, to, limit },
      headers: { 'X-Tenant-Id': tenantId }
    })
}

// ── Dashboard Config (per-user layout persistence) ────────────────────────────
export const dashboardConfigApi = {
  getLayout: () => api.get('/dashboard-config'),
  saveLayout: widgets => api.put('/dashboard-config', { widgets }),
}
